import EditIcon from '@mui/icons-material/Edit';
import { Dialog } from '@mui/material';
import DialogContent from '@mui/material/DialogContent';
import {
  selectContact,
  selectContactInvoicing,
  unSelectContact,
  unSelectContactInvoicing,
} from 'lib/api/contacts';
import { updateBusinessContact } from 'lib/api/contacts';
import { Contact, ContactWithBusiness } from 'lib/api/contacts/types';
import { getIntegration } from 'lib/api/integrations';
import UpdateContact from 'lib/common/Forms/Contacts/Update';
import {
  translateCountry,
  translateMaritalStatus,
} from 'lib/consts/contactFields';
import Table from 'lib/designSystem/Table';
import DialogTitle from 'lib/designSystem/Title/DialogTitle';
import { validatePermissions } from 'lib/functions/utils';
import React from 'react';
import { useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { AuthState } from 'services/redux/authSlice';

import DeleteContactDialog from './Components/DeleteContactDialog';
import ProfileCell from './Components/ProfileCell';

export type TableData = Contact | ContactWithBusiness;

type Props = {
  data: TableData[];
  handleClick?: (contactId: number) => void;
  showIdentifier?: boolean;
  showName?: boolean;
  showEmail?: boolean;
  showPhone?: boolean;
  showRole?: boolean;
  showObservation?: boolean;
  showCountry?: boolean;
  showProfession?: boolean;
  showMaritalStatus?: boolean;
  showBusiness?: boolean;
  showActions?: boolean;
  showProfiles?: boolean;
};

function ContactsTable({
  data,
  handleClick,
  showActions = true,
  showIdentifier = true,
  showName = true,
  showEmail = true,
  showPhone = true,
  showRole = true,
  showObservation = true,
  showCountry = true,
  showProfession = true,
  showMaritalStatus = true,
  showBusiness = true,
  showProfiles = true,
}: Props) {
  const dispatch = useDispatch();
  const user = useSelector((state: { auth: AuthState }) => state.auth.user);
  const { data: integration } = getIntegration(dispatch, 'digital-signature');
  const hasDs = integration?.status;

  const { mutate: selectMainFn } = selectContact(dispatch);
  const { mutate: unselectMainFn } = unSelectContact(dispatch);
  const { mutate: selectBillingFn } = selectContactInvoicing(dispatch);
  const { mutate: unselectBillingFn } = unSelectContactInvoicing(dispatch);
  const { mutate: updateContactFn } = updateBusinessContact(dispatch);

  const [toDelete, setToDelete] = useState<TableData | null>(null);
  const [toEdit, setToEdit] = useState<TableData | null>(null);

  const countryCell = (value: string) => {
    if (!value) return '-';
    return translateCountry(value);
  };

  const maritalStatusCell = (value: string) => {
    if (!value) return '-';
    return translateMaritalStatus(value);
  };

  const canEdit = () => validatePermissions(user, 'businesses_create_new');

  const check = (
    contactID: number,
    flag: 'selected' | 'invoicing' | 'isLegalRepresentative' | 'isPartner',
    mode: 'mark' | 'unmark',
  ): boolean => {
    if (!canEdit()) return false;
    const hasFlag = data?.some((x) => x.id === contactID && x[flag]);
    switch (mode) {
      case 'mark':
        return !hasFlag;
      case 'unmark':
        return !!hasFlag;
      default:
        return false;
    }
  };

  const handleAskDelete = (id: number) => {
    const contact = data.find((item) => item.id === id) ?? null;
    setToDelete(contact);
  };

  const handleAskEdit = (id: number) => {
    const contact = data.find((item) => item.id === id) ?? null;
    setToEdit(contact);
  };

  const handleMarkFlag = (
    id: number,
    flag: 'isLegalRepresentative' | 'isPartner',
    value: boolean,
  ) => {
    const contact = data.find((item) => item.id === id) ?? null;
    if (!contact) return;
    updateContactFn({ id: contact.id, [flag]: value });
  };

  const headers = [
    ...(showIdentifier
      ? [{ key: 'identifier', label: 'RUT', type: 'text' }]
      : []),
    ...(showName ? [{ key: 'name', label: 'Nombre' }] : []),
    ...(showEmail ? [{ key: 'email', label: 'Email', type: 'copyable' }] : []),
    ...(showPhone ? [{ key: 'phone', label: 'Teléfono' }] : []),
    ...(showRole ? [{ key: 'role', label: 'Rol' }] : []),
    ...(showBusiness
      ? [{ key: 'business', label: 'Empresa', type: 'dual-text' }]
      : []),
    ...(showCountry && hasDs
      ? [
          {
            key: 'country',
            label: 'País',
            customComponent: countryCell,
          },
        ]
      : []),
    ...(showProfession && hasDs
      ? [
          {
            key: 'profession',
            label: 'Profesión',
          },
        ]
      : []),
    ...(showMaritalStatus && hasDs
      ? [
          {
            key: 'maritalStatus',
            label: 'Estado civil',
            customComponent: maritalStatusCell,
          },
        ]
      : []),
    ...(showObservation
      ? [{ key: 'observation', label: 'Observaciones', type: 'text' }]
      : []),
    ...(showProfiles
      ? [
          {
            key: 'id',
            label: 'Perfiles',
            customComponent: (id: number) => (
              <ProfileCell id={id} data={data} />
            ),
          },
        ]
      : []),
  ];

  const actions = [
    {
      action: (id: number) => handleMarkFlag(id, 'isLegalRepresentative', true),
      actionLabel: 'Marcar como representante legal',
      canShow: (id: number) => check(id, 'isLegalRepresentative', 'mark'),
    },
    {
      action: (id: number) =>
        handleMarkFlag(id, 'isLegalRepresentative', false),
      actionLabel: 'Desmarcar como representante legal',
      canShow: (id: number) => check(id, 'isLegalRepresentative', 'unmark'),
    },
    {
      action: (id: number) => handleMarkFlag(id, 'isPartner', true),
      actionLabel: 'Marcar como socio',
      canShow: (id: number) => check(id, 'isPartner', 'mark'),
    },
    {
      action: (id: number) => handleMarkFlag(id, 'isPartner', false),
      actionLabel: 'Desmarcar como socio',
      canShow: (id: number) => check(id, 'isPartner', 'unmark'),
    },
    {
      action: selectMainFn,
      actionLabel: 'Marcar como principal',
      canShow: (id: number) => check(id, 'selected', 'mark'),
    },
    {
      action: unselectMainFn,
      actionLabel: 'Desmarcar como principal',
      canShow: (id: number) => check(id, 'selected', 'unmark'),
    },
    {
      action: selectBillingFn,
      actionLabel: 'Marcar como contacto de facturación',
      canShow: (id: number) => check(id, 'invoicing', 'mark'),
    },
    {
      action: unselectBillingFn,
      actionLabel: 'Desmarcar como contacto de facturación',
      canShow: (id: number) => check(id, 'invoicing', 'unmark'),
    },
    {
      action: handleAskEdit,
      actionLabel: 'Editar',
      canShow: canEdit,
    },
    {
      action: handleAskDelete,
      actionLabel: 'Eliminar',
      canShow: canEdit,
    },
  ];

  return (
    <>
      <Table
        data={data}
        dataHeaders={headers}
        actions={showActions ? actions : []}
        handleClickRow={handleClick}
        exportToFile="xlsx"
      />
      <DeleteContactDialog
        open={!!toDelete}
        contact={toDelete}
        onClose={() => setToDelete(null)}
      />
      <Dialog open={!!toEdit} onClose={() => setToEdit(null)}>
        <DialogTitle
          onClose={() => setToEdit(null)}
          title="Editar contacto"
          icon={<EditIcon color="primary" />}
          color="primary"
        />
        <DialogContent>
          <UpdateContact
            selectedContact={toEdit}
            onUpdated={() => setToEdit(null)}
          />
        </DialogContent>
      </Dialog>
    </>
  );
}

export default ContactsTable;
